import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { escape } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";

import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { fieldNonexistentOrMatchingFilter } from "../../../components/Shared";
import { ProgressCircle } from "../../../components/Shared/ProgressHandler/ProgressCircle/ProgressCircle";
import {
  IProgressColumn,
  IProgressVisualizationProps,
  ProgressHandler,
} from "../../../components/Shared/ProgressHandler/ProgressHandler";
import { translationKeys } from "../../../models/app-translation-keys";
import {
  IFileProgressError,
  IFileProgressSuccess,
  ILanguageOption,
  IProgressedFileResult,
} from "../../../models/interfaces";
import { useTranslationService } from "../../../services/ServiceHooks";
import { useStore } from "../../../stores/StoreContext";
import { TranslationContentLayout } from "../TranslationContentLayout/TranslationContentLayout";
import { IGlossaryFileSupportExtended } from "../TranslationHeader/GlossarySelection/GlossarySelection.types";
import { TranslationType } from "../TranslationHeader/TranslationHeader";

import "./TranslationContentText.scss";

import { InitOverrideFunction, SignalREventNames, TranslatorProviderType } from "../../../../@api";

interface ITranslationContentTextProps {
  sourceLanguage: ILanguageOption;
  targetLanguage: ILanguageOption;
  selectedGlossary: IGlossaryFileSupportExtended | null;
  selectedProviderType: TranslatorProviderType;
}

interface IProgressedTranslationFileResult extends IProgressedFileResult {
  outputFile?: Blob;
}

const MAX_INPUT_LENGTH = 10000;
const textTranslationKeys = translationKeys.feature.translation.type.text;

export const TranslationContentText: FunctionComponent<ITranslationContentTextProps> = observer(
  (props: ITranslationContentTextProps) => {
    const { t } = useTranslation();
    const translationService = useTranslationService();
    const { budgetStore, translationStore, userStore } = useStore();

    const [translationResult, setTranslationResult] = useState<IProgressedTranslationFileResult | undefined>(undefined);
    const [isSignalRActive, setIsSignalRActive] = useState<boolean>(false);

    const processIdRef = useRef<string | null>(null);
    const abortControllerRef = useRef<AbortController | undefined>(undefined);
    const translationInProgressRef = useRef<boolean>(false);
    const textInputAreaRef = useRef<HTMLTextAreaElement>(null);

    const handleBudgetLimitReached = useCallback((response: Response) => {
      budgetStore.setBudgetDisabled(true);
      budgetStore.errorCode = response.status;
    }, []);

    useEffect(() => {
      setTimeout(() => {
        if (textInputAreaRef?.current) {
          textInputAreaRef.current.focus();
          textInputAreaRef.current.click();
        }
      }, 100);
    }, [textInputAreaRef?.current]);

    useEffect(() => {
      if (!translationStore.isTextProcessing) {
        return;
      }

      if (!isSignalRActive && !!props.selectedGlossary?.glossaryId) {
        console.warn("[TextTranslation] SignalR connection is not active yet. Waiting...");
        return;
      }

      startTranslateText();
    }, [translationStore.isTextProcessing, isSignalRActive]);

    useEffect(() => {
      // Cleanup function to cancel ongoing text translation and stop open signalR connections before unmounting
      return () => {
        cancelFileUploadAndOpenProcesses();
      };
    }, []);

    const setDefaultProgressValues = () => {
      processIdRef.current = null;
      translationInProgressRef.current = false;
      translationStore.isTextProcessing = false;
    };

    const startProgressingText = () => {
      setDefaultProgressValues();

      // Process ID is generated to track the translation process via SignalR and for cancellation if needed
      processIdRef.current = uuidv4();
      translationInProgressRef.current = false;

      translationStore.isTextProcessing = true;
      setTranslationResult(undefined);
      setIsSignalRActive(false);
      translationStore.isTextProcessing = true;
    };

    const startTranslateText = async () => {
      abortControllerRef.current = new AbortController();

      const trimmedValue = translationStore.textToTranslate?.trim();
      if (!trimmedValue?.length) {
        setDefaultProgressValues();
        setTranslationResult(undefined);
        return;
      }

      try {
        const requestInitOverrides: RequestInit | InitOverrideFunction = {
          signal: abortControllerRef.current.signal,
        };

        const response = props.selectedGlossary?.glossaryId
          ? await translationService.translateTextWithGlossary(
              {
                processId: processIdRef.current!,
                sourceLanguageCode: props.sourceLanguage.code,
                targetLanguageCode: props.targetLanguage.code,
                glossaryId: props.selectedGlossary.glossaryId,
                body: escape(trimmedValue),
              },
              requestInitOverrides,
            )
          : await translationService.translateText(
              {
                processId: processIdRef.current!,
                sourceLanguageCode: props.sourceLanguage.code,
                targetLanguageCode: props.targetLanguage.code,
                textTranslationRequest: {
                  text: escape(trimmedValue),
                  translatorProviderType: props.selectedProviderType,
                },
              },
              requestInitOverrides,
            );

        abortControllerRef.current = undefined;

        if (response.raw.ok) {
          if (props.selectedGlossary?.glossaryId) {
            console.debug(
              "[TextTranslation] Text uploaded successfully and translation started. Waiting for SignalR events...",
            );
          } else {
            const body = await response.raw.json();
            console.debug("[TextTranslation] Text translated successfully.", response, body);

            const bodyText = Array.isArray(body) ? body.map((l: string) => l.trim()).join("\n") : body;

            const unescapedBodyText = bodyText.includes("&")
              ? new DOMParser().parseFromString(bodyText, "text/html").documentElement.textContent
              : bodyText;

            setTranslationResult({ successMessage: unescape(unescapedBodyText) });
            translationStore.isTextProcessing = false;
          }
        } else if (response.raw.status === 402) {
          cancelFileUploadAndOpenProcesses();

          handleBudgetLimitReached(response.raw);
          setTranslationResult({
            errorMessage: t(translationKeys.globalErrors.budgetErrors.outOfBudget),
          });
        } else {
          cancelFileUploadAndOpenProcesses();

          const error = await response.raw.json();
          console.error("[TextTranslation] File upload failed. Error: ", error);

          setTranslationResult({
            errorMessage: t(translationKeys.upload.error.uploadFailedMarkdown),
          });
        }
      } catch (error: unknown) {
        cancelFileUploadAndOpenProcesses();

        if (error === 499) {
          console.debug("[TextTranslation] File upload aborted");
        } else {
          console.error("[TextTranslation] Error trasnslating text:", error);
          setTranslationResult({
            errorMessage: t(translationKeys.upload.error.uploadFailedMarkdown),
          });
        }
      }
    };

    const cancelFileProcessingInBackend = useCallback(async (processId: string) => {
      try {
        await translationService.cancelActiveTranslation({ processId });

        console.debug("[TextTranslation] Text processing cancelled successfully.");
      } catch (error) {
        console.error("[TextTranslation] Failed to cancel text processing", error);
      }
    }, []);

    const cancelFileUploadAndOpenProcesses = useCallback(() => {
      if (!(translationStore.isTextProcessing && translationStore.textToTranslate && processIdRef.current)) {
        console.debug(
          "[TextTranslation] No process ID found when trying to cancel text translation and open processes.",
        );
        return;
      }

      console.debug("[TextTranslation] Cancel text upload and stop open signalR connections");
      abortControllerRef.current?.abort(499);

      if (translationInProgressRef.current) {
        cancelFileProcessingInBackend(processIdRef.current);
      }

      setDefaultProgressValues();
    }, [processIdRef.current]);

    const progressHandlerMemo = useMemo(() => {
      if (!translationStore.isTextProcessing || !translationStore.textToTranslate) {
        return;
      }

      if (!props.selectedGlossary?.glossaryId) {
        return (
          <ProgressCircle
            progressTitles={[translationKeys.upload.progress.translate]}
            progressValues={[0]}
            cancelProgress={cancelFileUploadAndOpenProcesses}
          />
        );
      }

      const processIdFilter = fieldNonexistentOrMatchingFilter("processId", processIdRef.current);

      const successCallback = async (res: IFileProgressSuccess) => {
        const text = res.result?.trim();
        if (!text.length) {
          console.error("[TextTranslation] returned no text.", res);
          setTranslationResult({
            errorMessage: t(textTranslationKeys.output.errorEmptyOutput),
          });
        } else {
          console.debug("[TextTranslation] succeeded: ", res, text);
          setTranslationResult({ successMessage: text.trim() });
        }

        setDefaultProgressValues();
      };

      const errorCallback = (res: IFileProgressError) => {
        console.error("[TextTranslation] failed: ", res);
        setTranslationResult({ errorMessage: `${res.error}` });

        setDefaultProgressValues();
      };

      const columns: IProgressColumn[] = [
        {
          titleKey: translationKeys.upload.progress.upload,
          progress: 0,
          hubName: "uploadfile",
          progressEndpoint: SignalREventNames.UploadFileProgress,
          successEndpoint: SignalREventNames.UploadFileSucceeded,
          errorEndpoint: SignalREventNames.UploadFileFailed,
          filter: [processIdFilter],
          successCallback: () => console.debug("[TextTranslation] Upload completed successfully"),
          errorCallback: () => console.debug("[TextTranslation] Upload failed"),
        },
        {
          titleKey: translationKeys.upload.progress.translate,
          progress: 0,
          hubName: "translation",
          progressEndpoint: SignalREventNames.TranslationProgress,
          successEndpoint: SignalREventNames.TranslationSucceeded,
          errorEndpoint: SignalREventNames.TranslationFailed,
          filter: [processIdFilter],
          progressCallback: () => {
            translationInProgressRef.current = true;
            console.debug("[TextTranslation] Translation is in progress...");
          },
          successCallback,
          errorCallback,
        },
      ];

      return (
        <ProgressHandler
          progresscolumns={columns}
          cancelProgress={cancelFileUploadAndOpenProcesses}
          connectionSetupCompleted={() => setIsSignalRActive(true)}
          progressVisualizationTemplate={(progress: IProgressVisualizationProps) => (
            <ProgressCircle
              progressTitles={progress.progressTitles}
              progressValues={progress.progressValues}
              cancelProgress={progress.cancelProgress}
            />
          )}
        />
      );
    }, [translationStore.isTextProcessing, translationStore.textToTranslate]);

    const sourceComponent = () => (
      <div className="source-text-area-input">
        <InputTextarea
          value={translationStore.textToTranslate}
          ref={textInputAreaRef}
          placeholder={t(textTranslationKeys.placeholder)}
          maxLength={MAX_INPUT_LENGTH}
          onChange={event => (translationStore.textToTranslate = event.target.value)}
          autoResize
        />

        {!!translationStore.textToTranslate?.length && (
          <Button
            icon={<FontAwesomeIcon icon={faXmark} />}
            rounded
            text
            aria-label="Delete input"
            onClick={() => (translationStore.textToTranslate = "")}
          />
        )}
      </div>
    );

    const targetComponentMemo = useMemo(() => {
      if (!translationResult) {
        return undefined;
      }

      return (
        <div className="target-text-area-output">
          {translationResult?.successMessage?.length ? (
            <InputTextarea autoResize readOnly value={translationResult.successMessage} />
          ) : null}

          {!!translationResult?.errorMessage && (
            <div className="error-container">
              <div className="title">{t(translationKeys.feature.translation.type.text.output.errorTitle)}</div>

              <div className="error-message">{translationResult.errorMessage}</div>
            </div>
          )}
        </div>
      );
    }, [translationResult]);

    return (
      <TranslationContentLayout
        type={TranslationType.Text}
        sourceArea={sourceComponent()}
        targetArea={targetComponentMemo}
        isTranslating={translationStore.isTextProcessing}
        customLoadingTemplate={progressHandlerMemo}
        translate={() => !translationStore.isTextProcessing && startProgressingText()}
        disabled={!translationStore.textToTranslate || translationStore.isTextProcessing || !budgetStore.hasBudget}
        showTranslationButton={true}
      />
    );
  },
);
